import { extend, objectDefinedNotNull } from "@pnp/common";
import { objectPath } from "./opactionbuilders";
import { property, staticProperty } from "./opbuilders";
import { writeObjectPathBody } from "./utils";
/**
 * Represents an ObjectPath used when querying ProcessQuery
 */
var ObjectPath = /** @class */ (function () {
    function ObjectPath(path, actions, id, replaceAfter) {
        if (actions === void 0) { actions = []; }
        if (id === void 0) { id = -1; }
        if (replaceAfter === void 0) { replaceAfter = []; }
        this.path = path;
        this.actions = actions;
        this.id = id;
        this.replaceAfter = replaceAfter;
    }
    return ObjectPath;
}());
export { ObjectPath };
/**
 * Replaces all found instance of the $$ID$$ placeholder in the supplied xml string
 *
 * @param id New value to be insterted
 * @param xml The existing xml fragment in which the replace should occur
 */
export function opSetId(id, xml) {
    return xml.replace(/\$\$ID\$\$/g, id);
}
/**
 * Replaces all found instance of the $$PATH_ID$$ placeholder in the supplied xml string
 *
 * @param id New value to be insterted
 * @param xml The existing xml fragment in which the replace should occur
 */
export function opSetPathId(id, xml) {
    return xml.replace(/\$\$PATH_ID\$\$/g, id);
}
/**
 * Replaces all found instance of the $$PARENT_ID$$ placeholder in the supplied xml string
 *
 * @param id New value to be insterted
 * @param xml The existing xml fragment in which the replace should occur
 */
export function opSetParentId(id, xml) {
    return xml.replace(/\$\$PARENT_ID\$\$/g, id);
}
/**
 * Replaces all found instance of the $$OP_PARAM_ID$$ placeholder in the supplied xml string
 *
 * @param map A mapping where [index] = replaced_object_path_id
 * @param xml The existing xml fragment in which the replace should occur
 * @param indexMapper Used when creating batches, not meant for direct use external to this library
 */
export function opSetPathParamId(map, xml, indexMapper) {
    if (indexMapper === void 0) { indexMapper = function (n) { return n; }; }
    // this approach works because input params must come before the things that need them
    // meaning the right id will always be in the map
    var matches = /\$\$OP_PARAM_ID_(\d+)\$\$/ig.exec(xml);
    if (matches !== null) {
        for (var i = 1; i < matches.length; i++) {
            var index = parseInt(matches[i], 10);
            var regex = new RegExp("\\$\\$OP_PARAM_ID_" + index + "\\$\\$", "ig");
            xml = xml.replace(regex, map[indexMapper(index)].toString());
        }
    }
    return xml;
}
/**
 * Represents a collection of IObjectPaths
 */
var ObjectPathQueue = /** @class */ (function () {
    function ObjectPathQueue(_paths, _relationships) {
        if (_paths === void 0) { _paths = []; }
        if (_relationships === void 0) { _relationships = {}; }
        this._paths = _paths;
        this._relationships = _relationships;
        this._contextIndex = -1;
        this._siteIndex = -1;
        this._webIndex = -1;
    }
    /**
     * Adds an object path to the queue
     *
     * @param op The action to add
     * @returns The index of the added object path
     */
    ObjectPathQueue.prototype.add = function (op) {
        this.dirty();
        this._paths.push(op);
        return this.lastIndex;
    };
    ObjectPathQueue.prototype.addChildRelationship = function (parentIndex, childIndex) {
        if (objectDefinedNotNull(this._relationships["_" + parentIndex])) {
            this._relationships["_" + parentIndex].push(childIndex);
        }
        else {
            this._relationships["_" + parentIndex] = [childIndex];
        }
    };
    ObjectPathQueue.prototype.getChildRelationship = function (parentIndex) {
        if (objectDefinedNotNull(this._relationships["_" + parentIndex])) {
            return this._relationships["_" + parentIndex];
        }
        else {
            return [];
        }
    };
    ObjectPathQueue.prototype.getChildRelationships = function () {
        return this._relationships;
    };
    /**
     * Appends an action to the supplied IObjectPath, replacing placeholders
     *
     * @param op IObjectPath to which the action will be appended
     * @param action The action to append
     */
    ObjectPathQueue.prototype.appendAction = function (op, action) {
        this.dirty();
        op.actions.push(action);
        return this;
    };
    /**
     * Appends an action to the last IObjectPath in the collection
     *
     * @param action
     */
    ObjectPathQueue.prototype.appendActionToLast = function (action) {
        return this.appendAction(this.last, action);
    };
    /**
     * Creates a linked copy of this ObjectPathQueue
     */
    ObjectPathQueue.prototype.copy = function () {
        var copy = new ObjectPathQueue(this.toArray(), extend({}, this._relationships));
        copy._contextIndex = this._contextIndex;
        copy._siteIndex = this._siteIndex;
        copy._webIndex = this._webIndex;
        return copy;
    };
    /**
     * Creates an independent clone of this ObjectPathQueue
     */
    ObjectPathQueue.prototype.clone = function () {
        var clone = new ObjectPathQueue(this.toArray().map(function (p) { return Object.assign({}, p); }), extend({}, this._relationships));
        clone._contextIndex = this._contextIndex;
        clone._siteIndex = this._siteIndex;
        clone._webIndex = this._webIndex;
        return clone;
    };
    /**
     * Gets a copy of this instance's paths
     */
    ObjectPathQueue.prototype.toArray = function () {
        return this._paths.slice(0);
    };
    Object.defineProperty(ObjectPathQueue.prototype, "last", {
        /**
         * The last IObjectPath instance added to this collection
         */
        get: function () {
            if (this._paths.length < 1) {
                return null;
            }
            return this._paths[this.lastIndex];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObjectPathQueue.prototype, "lastIndex", {
        /**
         * Index of the last IObjectPath added to the queue
         */
        get: function () {
            return this._paths.length - 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObjectPathQueue.prototype, "siteIndex", {
        /**
         * Gets the index of the current site in the queue
         */
        get: function () {
            if (this._siteIndex < 0) {
                // this needs to be here in case we create it
                var contextIndex = this.contextIndex;
                this._siteIndex = this.add(property("Site", 
                // actions
                objectPath()));
                this.addChildRelationship(contextIndex, this._siteIndex);
            }
            return this._siteIndex;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObjectPathQueue.prototype, "webIndex", {
        /**
         * Gets the index of the current web in the queue
         */
        get: function () {
            if (this._webIndex < 0) {
                // this needs to be here in case we create it
                var contextIndex = this.contextIndex;
                this._webIndex = this.add(property("Web", 
                // actions
                objectPath()));
                this.addChildRelationship(contextIndex, this._webIndex);
            }
            return this._webIndex;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObjectPathQueue.prototype, "contextIndex", {
        /**
         * Gets the index of the Current context in the queue, can be used to establish parent -> child rels
         */
        get: function () {
            if (this._contextIndex < 0) {
                this._contextIndex = this.add(staticProperty("Current", "{3747adcd-a3c3-41b9-bfab-4a64dd2f1e0a}", 
                // actions
                objectPath()));
            }
            return this._contextIndex;
        },
        enumerable: true,
        configurable: true
    });
    ObjectPathQueue.prototype.toBody = function () {
        if (objectDefinedNotNull(this._xml)) {
            return this._xml;
        }
        // create our xml payload
        this._xml = writeObjectPathBody(this.toIndexedTree());
        return this._xml;
    };
    /**
     * Conducts the string replacements for id, parent id, and path id
     *
     * @returns The tree with all string replacements made
     */
    ObjectPathQueue.prototype.toIndexedTree = function () {
        var _this = this;
        var builderIndex = -1;
        var lastOpId = -1;
        var idIndexMap = [];
        return this.toArray().map(function (op, index, arr) {
            var opId = ++builderIndex;
            // track the array index => opId relationship
            idIndexMap.push(opId);
            // do path replacements
            op.path = opSetPathParamId(idIndexMap, opSetId(opId.toString(), op.path));
            if (lastOpId >= 0) {
                // if we have a parent do the replace
                op.path = opSetParentId(lastOpId.toString(), op.path);
            }
            // rewrite actions with placeholders replaced
            op.actions = op.actions.map(function (a) {
                var actionId = ++builderIndex;
                return opSetId(actionId.toString(), opSetPathId(opId.toString(), a));
            });
            // handle any specific child relationships
            _this.getChildRelationship(index).forEach(function (childIndex) {
                // set the parent id for our non-immediate children, thus removing the token so it isn't overwritten
                arr[childIndex].path = opSetParentId(opId.toString(), arr[childIndex].path);
            });
            // and remember our last object path id for the parent replace above
            lastOpId = opId;
            return op;
        });
    };
    /**
     * Dirties this queue clearing any cached data
     */
    ObjectPathQueue.prototype.dirty = function () {
        this._xml = null;
    };
    return ObjectPathQueue;
}());
export { ObjectPathQueue };
//# sourceMappingURL=objectpath.js.map